import {
  Settings2,
  Send,
  UserCheck,
  Scale,
  RefreshCw,
  ClipboardList,
  GitBranch,
  PieChart,
  CalendarRange,
  Link2,
  FileBarChart,
  Eye,
  CalendarPlus,
  ShieldCheck,
  Clock,
  Sparkles,
} from "lucide-react";
import FeaturePageLayout from "./FeaturePageLayout";
import leaveHeroImg from "../../assets/images/features/leave-management-hero.png.png";

const workflowSteps = [
  {
    id: 1,
    title: "Configure Leave Policies",
    desc: "HR defines Casual, Sick, Earned, Maternity, Comp-off and other leave types with entitlements and rules in Brixigo.",
    icon: Settings2,
  },
  {
    id: 2,
    title: "Employee Applies via ESS",
    desc: "Employees submit leave requests from the self-service portal with dates, type and reason — no paper forms.",
    icon: Send,
  },
  {
    id: 3,
    title: "Manager Approval",
    desc: "Reporting managers review the leave queue, approve or reject based on team coverage and company policy.",
    icon: UserCheck,
  },
  {
    id: 4,
    title: "Balances & Calendar",
    desc: "Approved leave auto-updates balances. Holiday calendars and weekly offs are factored into working-day calculations.",
    icon: Scale,
  },
  {
    id: 5,
    title: "Payroll & Attendance Sync",
    desc: "Leave records flow into the attendance calendar and payroll run so salary proration stays accurate.",
    icon: RefreshCw,
  },
];

const keyBenefits = [
  { title: "Online leave application", desc: "Employees apply from ESS anytime — no emails or spreadsheets.", icon: CalendarPlus },
  { title: "Manager approval workflow", desc: "Structured approve/reject flow with visibility for HR and managers.", icon: GitBranch },
  { title: "Leave balance tracking", desc: "Real-time balances after every approved or cancelled request.", icon: PieChart },
  { title: "Multiple leave policies", desc: "CL, SL, EL, maternity, comp-off and custom types per organisation.", icon: ClipboardList },
  { title: "Holiday integration", desc: "Holiday master and weekly offs automatically excluded from leave days.", icon: CalendarRange },
  { title: "Payroll synchronization", desc: "Approved leave reflected in attendance and monthly payroll processing.", icon: Link2 },
  { title: "Leave reports", desc: "Utilisation, balance and department-wise leave reports on demand.", icon: FileBarChart },
  { title: "Transparent leave management", desc: "Employees see history, balances and approval status in one place.", icon: Eye },
];

const whatsIncluded = [
  { title: "ESS Leave Requests", desc: "Apply, track and cancel leave from the employee portal.", icon: Send },
  { title: "Approval Queue", desc: "Managers act on pending requests from Leave dashboard.", icon: UserCheck },
  { title: "Leave Type Setup", desc: "Configure policies, accruals and carry-forward rules.", icon: Settings2 },
  { title: "Auto Balance Update", desc: "Balances refresh after every approved transaction.", icon: Scale },
  { title: "Holiday Calendar", desc: "Org-wide holidays synced with leave calculations.", icon: CalendarRange },
  { title: "Attendance Integration", desc: "Leave days marked on attendance calendar automatically.", icon: RefreshCw },
  { title: "Leave History", desc: "Full audit trail of applications and approvals.", icon: Clock },
  { title: "Role-Based Access", desc: "HR, managers and employees see only what they need.", icon: ShieldCheck },
];

export default function LeaveManagement() {
  return (
    <FeaturePageLayout
      featureName="Leave Management"
      title="Leave Management"
      subtitle="Manage leave requests, approvals and balances — fully integrated with payroll."
      description="Brixigo provides a complete Leave Management system that helps organisations efficiently manage employee leave requests and approvals. Employees apply from ESS, managers approve in workflow, and approved leave syncs with attendance and payroll for accurate salary processing."
      heroImage={leaveHeroImg}
      heroImageAlt="Leave management illustration"
      workflowHeading="How Leave Management Works in Brixigo"
      workflowSteps={workflowSteps}
      benefits={keyBenefits}
      included={whatsIncluded}
      ctaIcon={Sparkles}
      ctaTitle="Simplify Leave. Empower Your Team."
      ctaDescription="Give employees self-service leave and give HR confidence that payroll and attendance stay in sync."
      ctaButtonLabel="Book a Demo"
    />
  );
}
